import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Vertebra, BrainField, Lead } from '../../types';
import * as api from '../../services/api';
import { SuppressionListShell } from './SuppressionListShell';
import { BigSkyEnrichmentShell } from './BigSkyEnrichmentShell';
import { OWNED_ATTRIBUTE_CATALOG } from './ownedAttributeCatalog';

// Viewport-aware tooltip. Renders the bubble via a portal at document.body
// so it escapes any ancestor with overflow:hidden (the modal shell) and
// clamps horizontally so it never overflows the viewport on either edge.
// The `align` prop is kept for backward compat but is no longer needed —
// positioning is automatic. Defaults to "above the icon" and flips below
// if there isn't room (near the top of the viewport).
function FieldTooltip({ text }: { text: string; align?: 'center' | 'right' }) {
  const [coords, setCoords] = useState<{ left: number; top: number; placeBelow: boolean } | null>(null);
  const iconRef = React.useRef<HTMLSpanElement>(null);

  const TOOLTIP_WIDTH = 280;
  const EDGE_MARGIN = 8;
  const TOOLTIP_EST_HEIGHT = 80;

  const handleEnter = () => {
    if (!iconRef.current) {return;}
    const rect = iconRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - EDGE_MARGIN));
    const placeBelow = rect.top < TOOLTIP_EST_HEIGHT + EDGE_MARGIN;
    const top = placeBelow ? rect.bottom + 6 : rect.top - 6;
    setCoords({ left, top, placeBelow });
  };

  return (
    <span
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setCoords(null)}
    >
      &#9432;
      {coords && createPortal(
        <span
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            transform: coords.placeBelow ? 'none' : 'translateY(-100%)',
            width: TOOLTIP_WIDTH,
            padding: '8px 10px',
            background: '#1f2937',
            color: '#f3f4f6',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontWeight: 'normal',
            whiteSpace: 'normal',
            textAlign: 'left',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

// ============================================================
// BrainFieldSelect — single-select dropdown of Brain fields
// ============================================================
// Replaces the old freeform "type the field name" text inputs across the modal
// (Free Email Check, Free Phone Check, Email Oversight, Filter Rules). Backing
// the choice with the Brain's discovered/configured fields means a sales person
// can't typo a header (e.g. `email` when the source sent `EmailAddress`) and
// silently break the vertebra. Same incident-driven safeguard as the dedup
// multi-select; see Lead.checkDuplicate for the runtime side of the fix.
//
// If `value` is a string that ISN'T in the Brain (existing vertebrae from
// before this UI was rolled out), the value still renders as a selected option
// but is marked stale in red and the caller's validation should block save.
function BrainFieldSelect({
  value,
  onChange,
  brainFields,
  required,
  placeholder,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  brainFields: BrainField[];
  required?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const brainFieldNames = brainFields.map(b => b.fieldName);
  const hasValue = !!value;
  const isStale = hasValue && !brainFieldNames.includes(value);
  const brainIsEmpty = brainFields.length === 0;
  const isMissing = !!required && !hasValue;
  const hasIssue = isStale || isMissing;

  return (
    <div style={style}>
      <select
        className="form-input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={hasIssue ? { borderColor: '#ef4444' } : undefined}
        disabled={brainIsEmpty}
      >
        <option value="">
          {brainIsEmpty
            ? '— No Brain fields yet —'
            : placeholder || '— Select a field from the Brain —'}
        </option>
        {/* If the saved value isn't in the Brain, surface it so the user can
            see what's currently configured (and why it's flagged). */}
        {isStale && (
          <option value={value}>{value} (not in Brain)</option>
        )}
        {brainFieldNames.map(f => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>
      {brainIsEmpty && (
        <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
          No Brain fields yet. Configure the Brain's data points first
          (or send a test lead) so they appear here.
        </div>
      )}
      {isStale && (
        <div style={{ color: '#92400e', fontSize: 12, marginTop: 4 }}>
          &#9888; "{value}" is not in this Brain. Incoming leads don't have
          this key, so the vertebra would silently treat it as empty. Pick a
          real Brain field.
        </div>
      )}
      {isMissing && (
        <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
          Required. Pick a field from the Brain.
        </div>
      )}
    </div>
  );
}

interface Props {
  vertebra: Vertebra;
  onSave: (config: Record<string, any>) => void;
  onClose: () => void;
}

function VertebraConfigModal({ vertebra, onSave, onClose }: Props) {
  // For suppression vertebrae, seed `suppressionField` with the obvious
  // default ('email' / 'phone') at mount time IF it's not already chosen.
  // The BrainFieldSelect used to render with `value={config.suppressionField
  // || defaultField}`, which displayed the default visually but never wrote
  // it into config — clicking Save without touching the dropdown then failed
  // validation with "Pick the Brain field..." Native <select> also doesn't
  // fire onChange when you re-pick the already-selected option, so the
  // workaround of "select it again" was a no-op. Materializing the default
  // into config at mount fixes both. If the Brain doesn't actually have a
  // field named 'email'/'phone', the existing stale-field check in
  // handleSave + the red "(not in Brain)" indicator in BrainFieldSelect
  // surface that clearly.
  const [config, setConfig] = useState<Record<string, any>>(() => {
    const base = vertebra.config || {};
    if (!base.suppressionField) {
      if (vertebra.type === 'email_suppression') {return { ...base, suppressionField: 'email' };}
      if (vertebra.type === 'phone_suppression') {return { ...base, suppressionField: 'phone' };}
    }
    return base;
  });
  // Raw text backing the Custom Headers (JSON) input — see the same pattern in
  // LegNodeConfigModal. Decoupled from the parsed object so mid-typing input
  // isn't discarded (otherwise the field is un-typeable).
  const [apiHeadersRaw, setApiHeadersRaw] = useState<string>(
    vertebra.config?.apiHeaders ? JSON.stringify(vertebra.config.apiHeaders) : ''
  );
  const [apiHeadersError, setApiHeadersError] = useState<string | null>(null);

  // Pass/fail stats + drill-down state.
  // Backend extends this to include a `today` block (pass/fail for leads
  // received since local midnight in the org's timezone) since 2026-05-18.
  // Loaded after the modal opens — while `statsLoading` is true the
  // header shows a small skeleton shimmer in the count positions so the
  // modal frame paints instantly. See [[feedback_deferred_load_pattern]].
  const [stats, setStats] = useState<{ pass: number; fail: number; today?: { pass: number; fail: number } } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [view, setView] = useState<'config' | 'leads'>('config');
  const [leadsFilter, setLeadsFilter] = useState<'pass' | 'fail'>('pass');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [brainFields, setBrainFields] = useState<BrainField[]>([]);

  const isApiType = ['email_validation', 'phone_validation', 'address_validation', 'data_enrichment', 'database_lookup', 'custom_api'].includes(vertebra.type);
  const isFilterType = vertebra.type === 'filter_rule';

  // Fetch lifetime pass/fail stats on mount + brain fields for the drill-down table.
  useEffect(() => {
    setStatsLoading(true);
    api.getVertebraStats(vertebra.dataBodyId, vertebra.id)
      .then(s => {
        setStats(s);
        setStatsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load vertebra stats:', err);
        setStats({ pass: 0, fail: 0 });
        setStatsLoading(false);
      });
    api.getBrainFields(vertebra.dataBodyId)
      .then(setBrainFields)
      .catch(err => console.error('Failed to load brain fields:', err));
  }, [vertebra.id, vertebra.dataBodyId]);

  function updateConfig(key: string, value: any) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    // Required-field validation for every vertebra type that targets one or more
    // Brain fields. Without this, a typo'd or stale field name resolves to
    // undefined at runtime and silently breaks the vertebra (empty SHA256 for
    // dedup, "field missing or empty" for the email/phone checks, always-false
    // for filter rules). Backend also guards (Lead.checkDuplicate warns/passes
    // through), but blocking at the UI catches the misconfig before any leads
    // run against it. See incident 2026-05-21 (SignalFound).
    const brainFieldNames = brainFields.map(b => b.fieldName);
    const isBrainField = (f: string) => brainFieldNames.includes(f);

    if (vertebra.type === 'dedup_check') {
      const fields: string[] = config.dedupFields || [];
      if (fields.length === 0) {
        alert('Pick at least one field to dedup on.');
        return;
      }
      const stale = fields.filter(f => !isBrainField(f));
      if (stale.length > 0) {
        alert(
          `These dedup fields don't exist in this Brain: ${stale.join(', ')}.\n\n` +
          `Incoming leads don't have those keys, so they'd produce empty dedup ` +
          `hashes and silently match every other empty-hash lead. Remove them ` +
          `(orange chips) and pick from the dropdown.`
        );
        return;
      }
    }

    // Single-field validations: free_email_check, free_phone_check,
    // email_oversight_validation, email_suppression, phone_suppression.
    // Each requires a single Brain field ('emailField' / 'phoneField' /
    // 'suppressionField') and must not be left blank.
    // `label` is the singular noun phrase used in user-facing alerts. We
    // pick wording that actually fits the data: "email address" reads fine,
    // but "phone address" doesn't — phones are numbers, not addresses.
    const singleFieldChecks: Array<{ types: string[]; key: string; label: string }> = [
      { types: ['free_email_check', 'email_oversight_validation'], key: 'emailField', label: 'email address' },
      { types: ['free_phone_check'], key: 'phoneField', label: 'phone number' },
      { types: ['email_suppression'], key: 'suppressionField', label: 'email address' },
      { types: ['phone_suppression'], key: 'suppressionField', label: 'phone number' },
    ];
    for (const check of singleFieldChecks) {
      if (!check.types.includes(vertebra.type)) {continue;}
      const value = (config[check.key] || '').trim();
      if (!value) {
        alert(`Pick the Brain field that contains the ${check.label}.`);
        return;
      }
      if (!isBrainField(value)) {
        alert(
          `"${value}" isn't a field in this Brain. Incoming leads don't have ` +
          `that key, so the vertebra would silently treat every lead as having ` +
          `a missing ${check.label}. Pick a real Brain field from the dropdown.`
        );
        return;
      }
    }

    // Filter rules: each rule.field must be a Brain field. (Rule with a stale
    // field would never match — every lead would fail the rule silently.)
    if (vertebra.type === 'filter_rule') {
      const rules: Array<{ field?: string }> = config.filterRules || [];
      const badRules = rules
        .map((r, i) => ({ i, field: (r.field || '').trim() }))
        .filter(r => !r.field || !isBrainField(r.field));
      if (badRules.length > 0) {
        const detail = badRules
          .map(r => `  Rule #${r.i + 1}: ${r.field ? `"${r.field}" not in Brain` : 'no field picked'}`)
          .join('\n');
        alert(
          `Some filter rules reference fields that don't exist in this Brain:\n\n` +
          `${detail}\n\n` +
          `Pick a Brain field for each rule (or remove the row).`
        );
        return;
      }
    }

    // BigSky / Owned enrichment: identical config — needs a source
    // identifier (real Brain field) and at least one attribute to enrich.
    // Credentials/data are server-side, so nothing key-related to validate.
    if (vertebra.type === 'bigsky_enrichment' || vertebra.type === 'owned_enrichment') {
      const sourceField = (config.enrichSourceField || '').trim();
      if (!config.enrichFrom) {
        alert('Pick what to enrich from (Email, Phone, MD5 or IP).');
        return;
      }
      if (!sourceField) {
        alert('Pick the Brain field that holds the value to enrich from.');
        return;
      }
      if (!isBrainField(sourceField)) {
        alert(
          `"${sourceField}" isn't a field in this Brain. Incoming leads don't have ` +
          `that key, so there'd be nothing to enrich from. Pick a real Brain field.`
        );
        return;
      }
      const attrs: string[] = Array.isArray(config.enrichAttributes) ? config.enrichAttributes : [];
      if (attrs.length === 0) {
        alert('Select at least one attribute to enrich.');
        return;
      }
    }

    onSave(config);
  }

  async function openLeadsView(filter: 'pass' | 'fail') {
    setLeadsFilter(filter);
    setView('leads');
    setLoadingLeads(true);
    try {
      const recent = await api.getVertebraLeads(vertebra.dataBodyId, vertebra.id, filter, 20);
      setLeads(recent);
    } catch (err) {
      console.error('Failed to load vertebra leads:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }

  function formatReceived(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: view === 'leads' ? 980 : 600,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header — always shows title, type, and pass/fail stats. The
            stats block is rendered with skeleton shimmer while stats
            load so the modal frame paints instantly. See
            [[feedback_deferred_load_pattern]]. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Configure: {vertebra.name}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap', marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280' }}>
              {statsLoading ? (
                <>
                  <span style={{ color: '#059669', fontSize: 11, fontWeight: 500 }}>
                    Pass: <span className="stat-skeleton" style={{ width: 40, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading pass count" />
                  </span>
                  <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 500 }}>
                    Failed: <span className="stat-skeleton" style={{ width: 40, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading fail count" />
                  </span>
                </>
              ) : stats ? (
                <>
                  <button
                    onClick={() => openLeadsView('pass')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#059669', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                    title="View 20 most recent leads that passed this vertebra"
                  >
                    Pass: {stats.pass.toLocaleString()}
                  </button>
                  <button
                    onClick={() => openLeadsView('fail')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#dc2626', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                    title="View 20 most recent leads that failed and stopped here"
                  >
                    Failed: {stats.fail.toLocaleString()}
                  </button>
                </>
              ) : null}
            </div>
            {statsLoading ? (
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6366f1' }}>
                <span>Today pass: <span className="stat-skeleton" style={{ width: 30, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading today pass" /></span>
                <span>Today failed: <span className="stat-skeleton" style={{ width: 30, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading today fail" /></span>
              </div>
            ) : stats?.today ? (
              <div
                style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6366f1' }}
                title="Pass/fail at this vertebra for leads received today (local midnight in the organization's timezone)."
              >
                <span>Today pass: {stats.today.pass.toLocaleString()}</span>
                <span>Today failed: {stats.today.fail.toLocaleString()}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
          Type: {vertebra.type.replace(/_/g, ' ')}
        </div>

        {/* Leads view — drill-down table */}
        {view === 'leads' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <button
                onClick={() => setView('config')}
                className="btn btn-secondary btn-sm"
              >
                &larr; Back to config
              </button>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Last 20 leads &middot; {leadsFilter === 'pass' ? 'passed this vertebra' : 'failed at this vertebra'} &middot; showing {leads.length}
              </div>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              {loadingLeads ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading...</div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No leads have {leadsFilter === 'pass' ? 'passed' : 'failed at'} this vertebra yet.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#f8f9fb', zIndex: 1, boxShadow: 'inset 0 -1px 0 #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Received</th>
                      {leadsFilter === 'fail' && (
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Reason</th>
                      )}
                      {brainFields.map(f => (
                        <th key={f.id} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{f.fieldLabel}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => (
                      <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatReceived(lead.receivedAt)}</td>
                        {leadsFilter === 'fail' && (
                          <td style={{ padding: '10px 12px', color: '#991b1b', fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.failReason || ''}>
                            {lead.failReason || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                          </td>
                        )}
                        {brainFields.map(f => {
                          // Enriched attributes (enrich_*, bigsky_*) live on the
                          // lead's enrichedData, NOT its original data — so read
                          // enrichedData first, then fall back to the source data.
                          const v = lead.enrichedData?.[f.fieldName] ?? lead.data?.[f.fieldName];
                          const display = Array.isArray(v) ? v.join(', ') : v;
                          return (
                            <td key={f.id} style={{ padding: '10px 12px', color: '#111827', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {display != null && display !== '' ? String(display) : <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}

        {/* Config view — wrap existing config in a conditional */}
        {view === 'config' && (
          <>

        {/* API Configuration */}
        {isApiType && (
          <>
            <div className="form-group">
              <label className="form-label">API URL</label>
              <input
                className="form-input"
                placeholder="https://api.example.com/validate"
                value={config.apiUrl || ''}
                onChange={e => updateConfig('apiUrl', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Method</label>
                <select
                  className="form-select"
                  value={config.apiMethod || 'POST'}
                  onChange={e => updateConfig('apiMethod', e.target.value)}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">API Key (Bearer token)</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="Your API key"
                  value={config.apiKey || ''}
                  onChange={e => updateConfig('apiKey', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Success Condition</label>
              <div className="form-row">
                <input
                  className="form-input"
                  placeholder="Response field (e.g., status)"
                  value={config.successCondition?.field || ''}
                  onChange={e => updateConfig('successCondition', {
                    ...config.successCondition,
                    field: e.target.value,
                    operator: config.successCondition?.operator || 'equals',
                    value: config.successCondition?.value || '',
                  })}
                  style={{ flex: 1 }}
                />
                <select
                  className="form-select"
                  value={config.successCondition?.operator || 'equals'}
                  onChange={e => updateConfig('successCondition', { ...config.successCondition, operator: e.target.value })}
                  style={{ width: 120 }}
                >
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="contains">Contains</option>
                  <option value="exists">Exists</option>
                </select>
                <input
                  className="form-input"
                  placeholder="Expected value"
                  value={config.successCondition?.value || ''}
                  onChange={e => updateConfig('successCondition', { ...config.successCondition, value: e.target.value })}
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Custom Headers (JSON)</label>
              <input
                className="form-input"
                placeholder='{"X-Custom-Header": "value"}'
                value={apiHeadersRaw}
                onChange={e => {
                  const v = e.target.value;
                  setApiHeadersRaw(v);
                  if (v.trim() === '') {
                    setApiHeadersError(null);
                    updateConfig('apiHeaders', undefined);
                    return;
                  }
                  try {
                    const parsed = JSON.parse(v);
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      setApiHeadersError(null);
                      updateConfig('apiHeaders', parsed);
                    } else {
                      setApiHeadersError('Headers must be a JSON object.');
                    }
                  } catch {
                    setApiHeadersError('Not valid JSON yet…');
                  }
                }}
              />
              {apiHeadersError && (
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{apiHeadersError}</div>
              )}
            </div>
          </>
        )}

        {/* Filter Rules */}
        {isFilterType && (
          <>
            <div className="form-group">
              <label className="form-label">Filter Rules (all must pass)</label>
              {(config.filterRules || []).map((rule: any, i: number) => (
                <div key={i} className="form-row" style={{ marginBottom: 8 }}>
                  <BrainFieldSelect
                    value={rule.field || ''}
                    onChange={v => {
                      const rules = [...(config.filterRules || [])];
                      rules[i] = { ...rules[i], field: v };
                      updateConfig('filterRules', rules);
                    }}
                    brainFields={brainFields}
                    required
                    placeholder="— Pick a Brain field —"
                    style={{ flex: 1 }}
                  />
                  <select
                    className="form-select"
                    value={rule.operator}
                    onChange={e => {
                      const rules = [...(config.filterRules || [])];
                      rules[i] = { ...rules[i], operator: e.target.value };
                      updateConfig('filterRules', rules);
                    }}
                    style={{ width: 130 }}
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Not Contains</option>
                    <option value="exists">Exists</option>
                    <option value="regex">Regex</option>
                  </select>
                  <input
                    className="form-input"
                    placeholder="Value"
                    value={rule.value}
                    onChange={e => {
                      const rules = [...(config.filterRules || [])];
                      rules[i] = { ...rules[i], value: e.target.value };
                      updateConfig('filterRules', rules);
                    }}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-icon"
                    onClick={() => {
                      const rules = (config.filterRules || []).filter((_: any, j: number) => j !== i);
                      updateConfig('filterRules', rules);
                    }}
                    style={{ color: '#ef4444' }}
                  >
                    x
                  </button>
                </div>
              ))}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const rules = [...(config.filterRules || []), { field: '', operator: 'equals', value: '' }];
                  updateConfig('filterRules', rules);
                }}
              >
                + Add Rule
              </button>
            </div>
          </>
        )}

        {/* Email Oversight Validation Config */}
        {vertebra.type === 'email_oversight_validation' && (() => {
          // Result codes from EO docs (Email Validation Result Table).
          // Codes 0 (Retry) and 12 (Unauthorized) are transient/error and not user-checkable.
          const EO_RESULTS: Array<{ id: number; name: string; description: string }> = [
            { id: 1,  name: 'Verified',         description: 'Email address is deliverable.' },
            { id: 2,  name: 'Undeliverable',    description: 'Does not exist, invalid, suspended, over quota, or disabled.' },
            { id: 3,  name: 'Catch All',        description: 'Domain accepts all mail — validity unverifiable. Send at your discretion.' },
            { id: 4,  name: 'Role',             description: 'Distribution group address (info@, sales@, no-reply@, test@).' },
            { id: 5,  name: 'Malformed',        description: 'Does not conform to valid email format.' },
            { id: 6,  name: 'SpamTrap',         description: 'Avoid at all cost — damages sender reputation.' },
            { id: 7,  name: 'Complainer',       description: 'Known to file complaints. Valid but not recommended.' },
            { id: 9,  name: 'Bot',              description: 'Maintained by bots for spam/clicking — not a real user.' },
            { id: 10, name: 'Seed Account',     description: 'Maintained for compliance tracking. Avoid sending.' },
            { id: 11, name: 'Unknown',          description: 'Cannot be verified at the moment. Retrying may succeed.' },
            { id: 13, name: 'Disposable Email', description: 'Temporary throwaway email account.' },
            { id: 20, name: 'Suppressed',       description: "In your account's suppression list (advanced validation only)." },
          ];
          const acceptable: number[] = Array.isArray(config.eoAcceptableResultIds) ? config.eoAcceptableResultIds : [1];
          const toggleResult = (id: number) => {
            const next = acceptable.includes(id) ? acceptable.filter(x => x !== id) : [...acceptable, id].sort((a, b) => a - b);
            updateConfig('eoAcceptableResultIds', next);
          };
          // Test button state lives outside React state so we don't pollute config — local component pattern.
          return <EmailOversightConfigSection
            config={config}
            updateConfig={updateConfig}
            dataBodyId={vertebra.dataBodyId}
            brainFields={brainFields}
            EO_RESULTS={EO_RESULTS}
            acceptable={acceptable}
            toggleResult={toggleResult}
          />;
        })()}

        {/* Free Email Check Config */}
        {vertebra.type === 'free_email_check' && (
          <>
            <div className="form-group">
              <label className="form-label">
                Email Field <span style={{ color: '#ef4444' }}>*</span>
                <FieldTooltip text="The Brain field containing the email address. Pick from the dropdown — values are case-sensitive and must match the incoming key exactly. (Previously a freeform text input that defaulted to 'email' if blank; that silently broke vertebrae whose source sent a differently-spelled field like 'EmailAddress'.)" />
              </label>
              <BrainFieldSelect
                value={config.emailField || ''}
                onChange={v => updateConfig('emailField', v)}
                brainFields={brainFields}
                required
                placeholder="— Select the email field —"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Rules applied
                <span
                  title="These rules run in order. The first rule that fails stops the check and the lead is rejected. The original email value in the lead is never modified — if the value passes, a normalized (lowercased, trimmed) form is written to enrichedData.email_normalized for downstream vertebrae and legs."
                  style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}
                >&#9432;</span>
              </label>
              <div style={{
                background: '#f8f9fb',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: '12px 16px',
                fontSize: 12,
                lineHeight: 1.7,
                color: '#374151',
              }}>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Trim whitespace, then enforce length 6&ndash;254 characters total.</li>
                  <li>Must contain exactly one <code>@</code>.</li>
                  <li>Local part (before <code>@</code>): 1&ndash;64 chars, only <code>a-z 0-9 . _ % + -</code>, no leading/trailing or consecutive dots.</li>
                  <li>Domain (after <code>@</code>): valid characters, no leading/trailing dots/hyphens, no consecutive dots, must include a TLD that&apos;s alphabetic and 2+ chars.</li>
                  <li>Reject disposable / temporary email domains (mailinator, guerrillamail, 10minutemail, yopmail, etc.).</li>
                  <li>Reject placeholder / test domains (<code>example.com</code>, <code>test.com</code>, etc.).</li>
                  <li>Reject role-based / distribution addresses (<code>info@</code>, <code>support@</code>, <code>noreply@</code>, <code>sales@</code>, <code>admin@</code>, etc.). Plus-tags (<code>john+stuff@</code>) are stripped before this check.</li>
                </ol>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                  On pass: a normalized form (lowercased, trimmed) is written to <code>enrichedData.email_normalized</code>. The original value in <code>data</code> stays untouched.
                </div>
              </div>
            </div>
          </>
        )}

        {/* Free Phone Check Config */}
        {vertebra.type === 'free_phone_check' && (
          <>
            <div className="form-group">
              <label className="form-label">
                Phone Field <span style={{ color: '#ef4444' }}>*</span>
                <FieldTooltip text="The Brain field containing the phone number. Pick from the dropdown — values are case-sensitive and must match the incoming key exactly. (Previously a freeform text input that defaulted to 'phone' if blank; that silently broke vertebrae whose source sent a differently-spelled field like 'PhoneNumber'.)" />
              </label>
              <BrainFieldSelect
                value={config.phoneField || ''}
                onChange={v => updateConfig('phoneField', v)}
                brainFields={brainFields}
                required
                placeholder="— Select the phone field —"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Rules applied
                <FieldTooltip text="These rules run in order. The first one that fails stops the check and the lead is rejected. The original phone value in the lead is never modified — on pass, a normalized E.164 form is written to enrichedData.phone_normalized for downstream use." />
              </label>
              <div style={{
                background: '#f8f9fb',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: '12px 16px',
                fontSize: 12,
                lineHeight: 1.7,
                color: '#374151',
              }}>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Strip formatting characters: parens, dashes, spaces, dots.</li>
                  <li>After stripping, only digits (and an optional leading <code>+</code>) may remain &mdash; otherwise reject.</li>
                  <li>Length: 10 digits (NANP), 11 with leading 1 (NANP), or 7&ndash;15 digits with <code>+</code> prefix (international).</li>
                  <li>NANP area code must start <code>2&ndash;9</code>; <code>555</code> rejected (not a real area code).</li>
                  <li>NANP exchange must start <code>2&ndash;9</code>; service codes (<code>211</code>, <code>311</code>, <code>411</code>, <code>511</code>, <code>611</code>, <code>711</code>, <code>811</code>, <code>911</code>) rejected.</li>
                  <li><code>555</code> exchange only allowed in <code>555-0100</code> through <code>555-0199</code> (FCC fictional-use range).</li>
                  <li>Reject all-same-digit and sequential patterns.</li>
                </ol>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                  On pass: a normalized form (e.g. <code>+15551234567</code>) is written to <code>enrichedData.phone_normalized</code>. The original value in <code>data</code> stays untouched.
                </div>
              </div>
            </div>
          </>
        )}

        {/* Suppression Config (email_suppression / phone_suppression) */}
        {(vertebra.type === 'email_suppression' || vertebra.type === 'phone_suppression') && (() => {
          const suppType: 'email' | 'phone' = vertebra.type === 'email_suppression' ? 'email' : 'phone';
          const label = suppType === 'email' ? 'Email' : 'Phone';
          return (
            <>
              <div className="form-group">
                <label className="form-label">
                  {label} Field <span style={{ color: '#ef4444' }}>*</span>
                  <FieldTooltip text={`The Brain field on the incoming lead containing the ${suppType} to check against your account suppression list. Pick from the dropdown — values are case-sensitive and must match the incoming key exactly.`} />
                </label>
                <BrainFieldSelect
                  value={config.suppressionField || ''}
                  onChange={v => updateConfig('suppressionField', v)}
                  brainFields={brainFields}
                  required
                  placeholder={`— Select the ${suppType} field —`}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  How it works
                  <FieldTooltip text={`If the lead's ${suppType} (after normalization) matches any entry on your account suppression list, the lead stops here. Otherwise it continues to the next vertebra. The list is shared across every data body in your account.`} />
                </label>
                <div style={{
                  background: '#f8f9fb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  padding: '12px 16px',
                  fontSize: 12,
                  lineHeight: 1.7,
                  color: '#374151',
                }}>
                  Incoming {suppType}s are normalized
                  {suppType === 'email'
                    ? ' (lowercased, trimmed)'
                    : ' to NANP E.164 form (e.g. +15551234567 — punctuation stripped)'}
                  {' '}before lookup. If a match is found on your account&apos;s
                  suppression list, the lead is blocked at this step. Unparseable
                  values (e.g. malformed {suppType}s) can&apos;t match anything and
                  pass through — let an upstream validation vertebra handle bad data.
                </div>
              </div>

              <SuppressionListShell type={suppType} />
            </>
          );
        })()}

        {/* BigSky / Owned Data Enrichment Config (identical UX) */}
        {(vertebra.type === 'bigsky_enrichment' || vertebra.type === 'owned_enrichment') && (() => {
          const isOwned = vertebra.type === 'owned_enrichment';
          const productLabel = isOwned ? 'Identity Enrichment' : 'BigSky Real Time Data Enrichment';
          // No default selection: leave this unset so NO pill is highlighted
          // until the user actually picks a source. The old `|| 'email'`
          // default made the Email pill look selected before anything was
          // chosen, so users never clicked it — and clicking is what seeds the
          // required field dropdown (onEnrichFromChange below). Empty = nothing
          // chosen yet; validation still requires a real selection.
          const enrichFrom: '' | 'email' | 'phone' | 'md5' | 'ip' = config.enrichFrom || '';
          const SOURCE_LABELS: Record<string, string> = { email: 'Email', phone: 'Phone', md5: 'MD5 Hash', ip: 'IP Address' };
          const sourceLabel = enrichFrom ? SOURCE_LABELS[enrichFrom] : '';
          const defaultFieldFor = (s: string) => s; // email→'email', etc.
          const onEnrichFromChange = (s: 'email' | 'phone' | 'md5' | 'ip') => {
            updateConfig('enrichFrom', s);
            // Seed the source field with the obvious default if not yet set,
            // so a freshly-dropped vertebra is usable in one click.
            if (!config.enrichSourceField) {updateConfig('enrichSourceField', defaultFieldFor(s));}
          };
          const selectedAttrs: string[] = Array.isArray(config.enrichAttributes) ? config.enrichAttributes : [];
          return (
            <>
              <div style={{
                background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6,
                padding: '12px 14px', fontSize: 12, lineHeight: 1.55, color: '#5b21b6', marginBottom: 16,
              }}>
                <strong>{productLabel}.</strong> Pick what to enrich <em>from</em>,
                tell us which Brain field holds that value, then choose the attributes to append.
                Leads missing that value (or with no match) pass straight through — this vertebra
                never blocks a lead. To require the value instead, mark its Brain field required.
              </div>

              <div className="form-group">
                <label className="form-label">
                  Enrich From <span style={{ color: '#ef4444' }}>*</span>
                  <FieldTooltip text="Which identifier we use to find a match. We call out with this value and append the attributes you select below." />
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(['email', 'phone', 'md5', 'ip'] as const).map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => onEnrichFromChange(s)}
                      style={{
                        padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                        border: enrichFrom === s ? '1px solid #7c3aed' : '1px solid #d1d5db',
                        background: enrichFrom === s ? '#7c3aed' : '#fff',
                        color: enrichFrom === s ? '#fff' : '#374151',
                        fontWeight: enrichFrom === s ? 600 : 400,
                      }}
                    >
                      {SOURCE_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>

              {enrichFrom ? (
                <div className="form-group">
                  <label className="form-label">
                    {sourceLabel} Field <span style={{ color: '#ef4444' }}>*</span>
                    <FieldTooltip text={`The Brain field on the incoming lead that contains the ${sourceLabel} to enrich from. Pick from the dropdown — case-sensitive, must match the incoming key exactly.`} />
                  </label>
                  <BrainFieldSelect
                    value={config.enrichSourceField || ''}
                    onChange={v => updateConfig('enrichSourceField', v)}
                    brainFields={brainFields}
                    required
                    placeholder={`— Select the ${sourceLabel.toLowerCase()} field —`}
                  />
                </div>
              ) : (
                <div className="form-group" style={{ fontSize: 12, color: '#6b7280' }}>
                  Pick what to enrich <em>from</em> above, then choose the Brain field that holds it.
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Attributes to enrich <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {/* Both vertebrae now return OUR canonical attributes (BigSky
                    maps the live vendor identity through the same adapter the
                    owned vertebra uses), so both pick from the canonical catalog
                    and the picker is identical. The old provisional BigSky
                    catalog is superseded. */}
                <BigSkyEnrichmentShell
                  selected={selectedAttrs}
                  onChange={next => updateConfig('enrichAttributes', next)}
                  catalog={OWNED_ATTRIBUTE_CATALOG}
                  showProvisionalNote={false}
                />
              </div>
            </>
          );
        })()}

        {/* Dedup Config */}
        {vertebra.type === 'dedup_check' && (() => {
          // Multi-select sourced from the Brain's data points. CSV/webhook headers
          // are preserved as-is (case-sensitive), so picking from the Brain guarantees
          // the configured field name exactly matches the incoming key. Replaces the
          // old freeform input that allowed silent typo'd configs (e.g. `email` while
          // the source sent `EmailAddress`), which collapsed every lead onto sha256('').
          const selectedFields: string[] = config.dedupFields || [];
          const brainFieldNames = brainFields.map(b => b.fieldName);
          const validSelected = selectedFields.filter(f => brainFieldNames.includes(f));
          const staleSelected = selectedFields.filter(f => !brainFieldNames.includes(f));
          const available = brainFieldNames.filter(f => !selectedFields.includes(f));
          const brainIsEmpty = brainFields.length === 0;

          const removeField = (f: string) =>
            updateConfig('dedupFields', selectedFields.filter(x => x !== f));
          const addField = (f: string) => {
            if (!f || selectedFields.includes(f)) {return;}
            updateConfig('dedupFields', [...selectedFields, f]);
          };

          const chipBase: React.CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            border: '1px solid transparent',
          };
          const validChip: React.CSSProperties = {
            ...chipBase,
            background: '#eef2ff',
            color: '#3730a3',
            borderColor: '#c7d2fe',
          };
          const staleChip: React.CSSProperties = {
            ...chipBase,
            background: '#fef3c7',
            color: '#92400e',
            borderColor: '#fcd34d',
          };
          const removeBtn: React.CSSProperties = {
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
            opacity: 0.7,
          };

          return (
            <>
            <div className="form-group">
              <label className="form-label">
                Deduplicate on fields <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 8 }}>
                Select one or more fields from the Brain. The dedup key is built from
                these values exactly as they arrive (case-sensitive).
              </div>

              {/* Selected chips */}
              {selectedFields.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {validSelected.map(f => (
                    <span key={f} style={validChip}>
                      {f}
                      <button
                        type="button"
                        style={removeBtn}
                        onClick={() => removeField(f)}
                        aria-label={`Remove ${f}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  {staleSelected.map(f => (
                    <span
                      key={f}
                      style={staleChip}
                      title={`"${f}" is not in this Brain. Incoming leads don't have this key, so it will never produce a usable dedup hash. Remove it and pick a real Brain field.`}
                    >
                      &#9888; {f} <em style={{ fontWeight: 400, fontSize: 11 }}>not in Brain</em>
                      <button
                        type="button"
                        style={removeBtn}
                        onClick={() => removeField(f)}
                        aria-label={`Remove ${f}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add-field dropdown */}
              {brainIsEmpty ? (
                <div
                  style={{
                    padding: '0.6rem 0.75rem',
                    border: '1px dashed #d1d5db',
                    borderRadius: 6,
                    color: '#6b7280',
                    fontSize: 13,
                    background: '#f9fafb',
                  }}
                >
                  No Brain fields yet. Configure the Brain's data points first
                  (or send a test lead) so they appear here.
                </div>
              ) : available.length === 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  All Brain fields are already selected.
                </div>
              ) : (
                <select
                  className="form-input"
                  value=""
                  onChange={e => {
                    addField(e.target.value);
                    // Reset the <select> so the same option can be re-picked after removal
                    e.target.value = '';
                  }}
                  style={
                    selectedFields.length === 0 ? { borderColor: '#ef4444' } : undefined
                  }
                >
                  <option value="" disabled>
                    + Add a field from the Brain&hellip;
                  </option>
                  {available.map(f => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              )}

              {selectedFields.length === 0 && (
                <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.4rem' }}>
                  Required. Pick at least one field to dedup on.
                </div>
              )}
              {staleSelected.length > 0 && (
                <div style={{ color: '#92400e', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                  Remove the orange &#9888; chip(s) above &mdash; those field names
                  aren't in this Brain, so they'd produce empty dedup hashes and
                  silently match every other empty-hash lead.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Lookback window (hours)</label>
              <input
                className="form-input"
                type="number"
                value={config.dedupWindow || 24}
                onChange={e => updateConfig('dedupWindow', parseInt(e.target.value))}
              />
            </div>
            </>
          );
        })()}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>Save Configuration</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// EmailOversightConfigSection — config UI for Email Oversight Validation
// ============================================================
// Lives below VertebraConfigModal because it has its own local state (the
// Test button's result, and a "show api token" toggle). Keeping it as a
// sub-component keeps the parent modal lean.
function EmailOversightConfigSection({
  config,
  updateConfig,
  dataBodyId,
  brainFields,
  EO_RESULTS,
  acceptable,
  toggleResult,
}: {
  config: Record<string, any>;
  updateConfig: (key: string, value: any) => void;
  dataBodyId: string;
  brainFields: BrainField[];
  EO_RESULTS: Array<{ id: number; name: string; description: string }>;
  acceptable: number[];
  toggleResult: (id: number) => void;
}) {
  const [showToken, setShowToken] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; raw?: any } | null>(null);
  const [egressIp, setEgressIp] = useState<string | null>(null);
  const [egressIpError, setEgressIpError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getEgressIp()
      .then(r => { if (!cancelled) {setEgressIp(r.ip);} })
      .catch((err: any) => { if (!cancelled) {setEgressIpError(err?.message || 'Could not detect server IP');} });
    return () => { cancelled = true; };
  }, []);

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const out = await api.testEmailOversight(
        dataBodyId,
        config.eoApiToken || '',
        config.eoListId || '',
        testEmail || ''
      );
      const httpOk = out.httpStatus >= 200 && out.httpStatus < 300;
      const r = out.response || {};
      const ok = httpOk && typeof r.ResultId === 'number';
      setTestResult({
        ok,
        message: ok
          ? `OK — got ${r.Result} (ResultId ${r.ResultId}) for ${r.Email}`
          : `Failed — HTTP ${out.httpStatus}${r.Error ? ': ' + r.Error : ''}${r.Message ? ' — ' + r.Message : ''}`,
        raw: out,
      });
    } catch (err: any) {
      setTestResult({ ok: false, message: 'Test request failed: ' + (err.message || 'unknown error') });
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
        padding: '12px 14px',
        fontSize: 12,
        lineHeight: 1.55,
        color: '#1e3a8a',
        marginBottom: 16,
      }}>
        <strong>Email Oversight</strong> classifies each email as Verified, Undeliverable, Catch All, Role, SpamTrap, Disposable, etc. You bring your own API Token + List ID from your account at <a href="https://login.emailoversight.com" target="_blank" rel="noopener noreferrer" style={{ color: '#1e40af' }}>login.emailoversight.com</a> &mdash; we send the lead&apos;s email to their API and pass/fail this vertebra based on which result codes you&apos;ve marked as acceptable below. The full classification is also written to <code>enrichedData.email_oversight</code> for downstream legs (e.g. route Catch All to a different destination).
      </div>

      <div style={{
        background: '#fef9c3',
        border: '1px solid #fbbf24',
        borderRadius: 6,
        padding: '12px 14px',
        fontSize: 12,
        lineHeight: 1.55,
        color: '#78350f',
        marginBottom: 16,
      }}>
        <strong>⚠ Whitelist this IP at Email Oversight before testing.</strong> EO blocks API calls from non-whitelisted source IPs (you&apos;ll see <code>HTTP 403 — Unauthorized ListId</code>). Add this IP in your EO dashboard under <em>API Access Control → IP Whitelist</em>:
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{
            background: '#fff',
            padding: '4px 10px',
            borderRadius: 4,
            border: '1px solid #fcd34d',
            fontSize: 13,
            fontFamily: 'monospace',
            fontWeight: 600,
            color: '#1f2937',
          }}>
            {egressIp ?? (egressIpError ? 'unable to detect' : 'detecting…')}
          </code>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => { if (egressIp) {void navigator.clipboard?.writeText(egressIp);} }}
            style={{ padding: '4px 10px', fontSize: 12, opacity: egressIp ? 1 : 0.5 }}
            title="Copy IP to clipboard"
            disabled={!egressIp}
          >
            Copy
          </button>
        </div>
        {egressIpError && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#92400e' }}>
            Couldn&apos;t auto-detect the server IP ({egressIpError}). Check the server&apos;s outbound IP manually before whitelisting.
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">
          API Token
          <span title="From Email Oversight: API Access Control → API Key. Specific to your account." style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}>&#9432;</span>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            type={showToken ? 'text' : 'password'}
            placeholder="cbfff2f3-b217-4472-...."
            value={config.eoApiToken || ''}
            onChange={e => updateConfig('eoApiToken', e.target.value)}
            style={{ flex: 1 }}
            autoComplete="off"
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setShowToken(s => !s)}
            style={{ width: 80 }}
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">
            List ID
            <span title="From Email Oversight: Lists menu. The numeric ID of the list this validation should be scoped to." style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}>&#9432;</span>
          </label>
          <input
            className="form-input"
            type="number"
            placeholder="25"
            value={config.eoListId ?? ''}
            onChange={e => updateConfig('eoListId', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">
            Email Field <span style={{ color: '#ef4444' }}>*</span>
            <span title="The Brain field containing the email address. Pick from the dropdown — values are case-sensitive and must match the incoming key exactly." style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}>&#9432;</span>
          </label>
          <BrainFieldSelect
            value={config.emailField || ''}
            onChange={v => updateConfig('emailField', v)}
            brainFields={brainFields}
            required
            placeholder="— Select the email field —"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Test connection</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            type="email"
            placeholder="real@email.com (sends a real API call to validate setup)"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={testing || !config.eoApiToken || !config.eoListId || !testEmail}
            onClick={runTest}
            style={{ width: 100 }}
          >
            {testing ? 'Testing...' : 'Test'}
          </button>
        </div>
        {testResult && (
          <div style={{
            marginTop: 8,
            padding: '8px 12px',
            background: testResult.ok ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${testResult.ok ? '#a7f3d0' : '#fecaca'}`,
            borderRadius: 6,
            fontSize: 12,
            color: testResult.ok ? '#065f46' : '#991b1b',
          }}>
            {testResult.message}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">
          Acceptable Results
          <span title="Check the result codes that should count as a PASS. Leads with any unchecked result will fail this vertebra. Default: only 'Verified' passes." style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}>&#9432;</span>
        </label>
        <div style={{
          background: '#f8f9fb',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '10px 14px',
          fontSize: 12,
          maxHeight: 320,
          overflowY: 'auto',
        }}>
          {EO_RESULTS.map(r => {
            const checked = acceptable.includes(r.id);
            return (
              <label key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleResult(r.id)}
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: checked ? '#059669' : '#374151' }}>
                    {r.id}. {r.name}
                  </span>
                  <span style={{ color: '#6b7280', marginLeft: 6 }}>&mdash; {r.description}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          Default selection: <strong>Verified only</strong>. Most lead-gen sources should keep it strict; loosen only if your downstream system can handle Catch All or Role addresses.
        </div>
      </div>
    </>
  );
}

export default VertebraConfigModal;
